/**
 * Product Item Carousel - Lightweight Slyde implementation for product cards
 * Enables swipe functionality on mobile for product card images (up to 3 images)
 */

(function() {
  const { Slyde, Pagination } = window.Slyde || {};

  if (!Slyde) {
    console.error('Slyde library not loaded');
    return;
  }

  class ProductItemCarousel extends HTMLElement {
    constructor() {
      super();
      this._slyde = null;
      this._pagination = null;
      this._resizeObserver = null;
      this._isMobile = false;
    }

    connectedCallback() {
      // Check if mobile on load
      this._checkMobile();

      // Initialize on mobile only
      if (this._isMobile) {
        requestAnimationFrame(() => this._init());
      }

      // Watch for resize to enable/disable carousel
      this._setupResizeObserver();
    }

    disconnectedCallback() {
      this._destroy();

      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
    }

    _checkMobile() {
      // Enable carousel only on touch devices or narrow screens
      this._isMobile = window.innerWidth < 741 || ('ontouchstart' in window);
    }

    _setupResizeObserver() {
      let resizeTimeout;

      const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          const wasMobile = this._isMobile;
          this._checkMobile();

          // Initialize if now mobile and wasn't before
          if (this._isMobile && !wasMobile && !this._slyde) {
            this._init();
          }
          // Destroy if now desktop and was mobile
          else if (!this._isMobile && wasMobile && this._slyde) {
            this._destroy();
            this._resetToFirstSlide();
          }
        }, 150);
      };

      window.addEventListener('resize', handleResize);

      // Store cleanup function
      this._cleanupResize = () => {
        window.removeEventListener('resize', handleResize);
        clearTimeout(resizeTimeout);
      };
    }

    _init() {
      const wrapper = this.querySelector('.product-item__carousel-wrapper');
      if (!wrapper) return;

      const slides = wrapper.querySelectorAll('.product-item__carousel-slide');
      if (slides.length <= 1) return;

      // Get pagination container
      this._pagination = this.closest('.product-item__image-wrapper')?.querySelector('.product-item__pagination');

      // Initialize Slyde with minimal config for performance
      this._slyde = new Slyde(this, {
        loop: false,
        navigation: false,
        pagination: false,
        thumbnails: false,
        video: false,
        slidesPerView: 1,
        spaceBetween: 0,
        freeMode: false
      });

      // Update pagination on slide change
      this._slyde.on('slideChange', () => this._updatePagination());

      // Initial pagination state
      this._updatePagination();
    }

    _updatePagination() {
      if (!this._pagination || !this._slyde) return;

      const dots = this._pagination.querySelectorAll('.product-item__pagination-dot');
      const currentIndex = this._slyde.currentIndex;

      dots.forEach((dot, index) => {
        dot.classList.toggle('product-item__pagination-dot--active', index === currentIndex);
      });
    }

    _resetToFirstSlide() {
      const wrapper = this.querySelector('.product-item__carousel-wrapper');
      if (wrapper) {
        wrapper.style.transform = '';
      }

      // Reset pagination to first dot
      if (this._pagination) {
        const dots = this._pagination.querySelectorAll('.product-item__pagination-dot');
        dots.forEach((dot, index) => {
          dot.classList.toggle('product-item__pagination-dot--active', index === 0);
        });
      }
    }

    _destroy() {
      if (this._slyde) {
        try {
          this._slyde.destroy();
        } catch (e) {}
        this._slyde = null;
      }

      if (this._cleanupResize) {
        this._cleanupResize();
      }
    }

    // Public API
    next() {
      this._slyde?.next();
    }

    prev() {
      this._slyde?.prev();
    }

    goTo(index) {
      this._slyde?.goTo(index);
    }

    get currentIndex() {
      return this._slyde?.currentIndex || 0;
    }
  }

  if (!customElements.get('product-item-carousel')) {
    customElements.define('product-item-carousel', ProductItemCarousel);
  }
})();
